import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card, Alert, Loader, Badge, Table, Select, Checkbox, Dialog } from '@dreamtree-org/twreact-ui';
import {
  DatabaseZap, Play, CheckCircle2, RefreshCw, PlugZap, ArrowRightLeft,
  Search, X, Plus, ChevronRight, Eraser, ListChecks, SkipForward, Lightbulb, Trash2,
} from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { storageKey } from '@/lib/storageKey';

const STATUS_VARIANT = {
  pending: 'default',
  running: 'primary',
  success: 'success',
  failed: 'error',
  skipped: 'warning',
};

// Statuses a step can be (re)started from. "running" is included for a step left
// mid-copy by an interrupted run — it resumes from its checkpoint. "skipped" is
// deliberately absent: a skipped step is resolved and never blocks the rest.
const RUNNABLE = ['pending', 'failed', 'running'];

const RUN_STATUS_VARIANT = {
  planned: 'default',
  running: 'primary',
  completed: 'success',
  partial: 'warning',
  failed: 'error',
};
// "partial" = the run finished but some tables were skipped, so it is not a clean
// copy. Say so rather than showing a green "completed".
const RUN_STATUS_LABEL = { partial: 'completed with skips' };

// Terminal statuses — only these are swept by "Clear finished". A "running" or
// "planned" run is left alone.
const FINISHED = ['completed', 'partial', 'failed'];

const MODES = [
  { value: 'upsert', label: 'Upsert — insert new + update existing' },
  { value: 'insert_missing', label: 'Insert missing only — never touch existing target rows' },
];

// One-click bundles for this app's own catalogs. Tables absent from the source are
// dropped silently, and the backend re-orders by FK at plan time regardless.
const PRESETS = [
  { label: 'RBAC catalog', tables: ['roles', 'permissions', 'role_permissions'] },
  { label: 'RBAC + user grants', tables: ['roles', 'permissions', 'role_permissions', 'user_roles', 'user_permissions', 'resource_grants'] },
  { label: 'Settings', tables: ['settings'] },
];

const SRC_KEY = storageKey('datamig:sourceUrl');
const TGT_KEY = storageKey('datamig:targetUrl');

const errText = (ex, fallback) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || fallback;

// The following three are pulled out of the JSX rather than inlined: this repo's lint
// config rejects nested ternaries, and each of these is a genuine three-way choice.

/** Rows actually written, or the read progress of a step still in flight. */
const copiedCell = (s) => {
  if (s.status === 'success') return s.rows_written;
  if (s.rows_read > 0) return <span className="text-primary-600">{s.rows_read} read</span>;
  return '—';
};

/** A step holding a checkpoint resumes rather than restarting from row zero. */
const runLabel = (s) => {
  if (s.resume_cursor) return 'Resume';
  return s.status === 'failed' ? 'Retry' : 'Run';
};

/** A run with skips is not a clean copy, so the bar shouldn't read as fully green. */
const progressColor = (run, skipped) => {
  if (run.failed_steps > 0) return 'bg-error-500';
  return skipped > 0 ? 'bg-warning-500' : 'bg-success-500';
};

/**
 * Order table names parents-before-children from the introspected FK graph (Kahn's
 * algorithm). This mirrors the backend's topoSort purely so the picker can PREVIEW the
 * copy order — the backend re-sorts at plan time and stays authoritative. Cycles fall
 * back to input order rather than dropping tables.
 */
const fkOrder = (tables) => {
  const map = {};
  tables.forEach((t) => { map[t.table] = t; });
  const names = tables.map((t) => t.table);
  const set = new Set(names);
  const indeg = new Map(names.map((n) => [n, 0]));
  const children = new Map(names.map((n) => [n, []]));
  for (const child of names) {
    for (const fk of map[child].foreignKeys || []) {
      const parent = fk.refTable;
      if (parent === child || !set.has(parent)) continue;
      children.get(parent).push(child);
      indeg.set(child, indeg.get(child) + 1);
    }
  }
  const queue = names.filter((n) => indeg.get(n) === 0);
  const ordered = [];
  while (queue.length) {
    const n = queue.shift();
    ordered.push(n);
    for (const c of children.get(n)) {
      indeg.set(c, indeg.get(c) - 1);
      if (indeg.get(c) === 0) queue.push(c);
    }
  }
  if (ordered.length < names.length) for (const n of names) if (!ordered.includes(n)) ordered.push(n);
  return ordered;
};

/**
 * Utility Tools › Data Migration.
 *
 * Copy ROW DATA from a source database into a target. Pick tables, choose a write mode,
 * plan to see source/target counts, then run table by table. Rows are matched by primary
 * key and tables copy parents-before-children. The server enforces step order.
 */
export default function DataMigration() {
  const { fetchApi } = useAppApi();
  const navigate = useNavigate();

  const [sourceUrl, setSourceUrl] = useState(() => localStorage.getItem(SRC_KEY) || '');
  const [targetUrl, setTargetUrl] = useState(() => localStorage.getItem(TGT_KEY) || '');
  const [mode, setMode] = useState('upsert');
  // Default on: a full clone should copy rows even when a parent table isn't there yet.
  // Off means strict FK enforcement, so children fail without their parent.
  const [disableFk, setDisableFk] = useState(true);

  const [testing, setTesting] = useState(null);
  const [allTables, setAllTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [loadedLabel, setLoadedLabel] = useState('');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  const [planning, setPlanning] = useState(false);
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [executingId, setExecutingId] = useState(null);
  const [skippingId, setSkippingId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'clear' | 'delete', run? }

  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { localStorage.setItem(SRC_KEY, sourceUrl); }, [sourceUrl]);
  useEffect(() => { localStorage.setItem(TGT_KEY, targetUrl); }, [targetUrl]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchApi('dataMigrationRuns');
      setHistory((res && res.data) || []);
    } catch {
      // Non-fatal — history is a convenience.
    } finally {
      setLoadingHistory(false);
    }
  }, [fetchApi]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Introspecting the source opens a real connection, so it is explicit — never on mount.
  const loadTables = async () => {
    const url = sourceUrl.trim();
    setErr('');
    setNotice('');
    if (!url) { setErr('Enter the source connection URL first.'); return; }
    setTablesLoading(true);
    try {
      const res = await fetchApi('dataMigrationTables', { url });
      const tables = res?.tables || [];
      setAllTables(tables);
      setLoadedLabel(res?.label || '');
      // Drop selections the new source doesn't have.
      const names = new Set(tables.map((t) => t.table));
      setSelected((prev) => prev.filter((t) => names.has(t)));
      setNotice(`${tables.length} table(s) loaded from ${res?.label || 'source'}.`);
    } catch (ex) {
      setAllTables([]);
      setLoadedLabel('');
      setErr(errText(ex, 'Could not read the source schema'));
    } finally {
      setTablesLoading(false);
    }
  };

  const testConnection = async (which) => {
    const url = (which === 'source' ? sourceUrl : targetUrl).trim();
    setErr('');
    setNotice('');
    if (!url) { setErr(`Enter the ${which} connection URL.`); return; }
    setTesting(which);
    try {
      const res = await fetchApi('dataMigrationTest', { url });
      setNotice(`${which} OK — ${res.engine} · ${res.label}`);
    } catch (ex) {
      setErr(errText(ex, 'Could not connect'));
    } finally {
      setTesting(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? allTables.filter((t) => t.table.toLowerCase().includes(q)) : allTables;
    return list.slice(0, 300);
  }, [allTables, search]);

  const toggle = (table) =>
    setSelected((prev) => (prev.includes(table) ? prev.filter((t) => t !== table) : [...prev, table]));

  const autoSelectAll = () => {
    if (!allTables.length) { setErr('Load the source tables first.'); return; }
    const ordered = fkOrder(allTables);
    setSelected(ordered);
    setNotice(`${ordered.length} table(s) auto-selected in FK order.`);
  };

  const addPreset = (tables) => {
    if (!allTables.length) { setErr('Load the source tables first.'); return; }
    const known = new Set(allTables.map((t) => t.table));
    const add = tables.filter((t) => known.has(t));
    if (!add.length) { setErr('None of those tables exist in the source schema.'); return; }
    setSelected((prev) => Array.from(new Set([...prev, ...add])));
  };

  const nextStepId = useMemo(() => {
    const next = steps.find((s) => RUNNABLE.includes(s.status));
    return next ? next.id : null;
  }, [steps]);

  const plan = async () => {
    setErr('');
    setNotice('');
    if (!sourceUrl.trim() || !targetUrl.trim()) { setErr('Enter both the source and target connection URLs.'); return; }
    if (!selected.length) { setErr('Select at least one table to migrate.'); return; }
    setPlanning(true);
    setRun(null);
    setSteps([]);
    try {
      const res = await fetchApi('dataMigrationPlan', {
        sourceUrl: sourceUrl.trim(),
        targetUrl: targetUrl.trim(),
        tables: selected,
        mode,
        disableFkChecks: disableFk,
      });
      setRun(res.run);
      setSteps(res.steps || []);
      setNotice(`${res.steps?.length || 0} table(s) ready to copy.`);
      loadHistory();
    } catch (ex) {
      setErr(errText(ex, 'Could not plan migration'));
    } finally {
      setPlanning(false);
    }
  };

  // `silent` suppresses the per-step message so Run all can show one summary instead.
  const runStep = async (step, { silent = false } = {}) => {
    if (!run) return null;
    setExecutingId(step.id);
    try {
      const res = await fetchApi('dataMigrationExecuteStep', { runId: run.id, stepId: step.id });
      setRun(res.run);
      setSteps(res.steps || []);
      if (!silent) {
        if (res.lastStep?.ok) {
          const s = res.lastStep;
          const parts = [];
          if (s.inserted != null) parts.push(`${s.inserted} new`);
          if (s.updated != null) parts.push(`${s.updated} updated`);
          if (s.skipped) parts.push(`${s.skipped} unchanged`);
          const summary = parts.length ? parts.join(', ') : `${s.rowsWritten} rows`;
          setNotice(`${step.table_name} · ${summary}`);
        } else {
          setErr(res.lastStep?.error || 'Copy failed.');
        }
        loadHistory();
      }
      return res;
    } catch (ex) {
      if (!silent) setErr(errText(ex, 'Could not run step'));
      return null;
    } finally {
      setExecutingId(null);
    }
  };

  const skipStep = async (step, { silent = false } = {}) => {
    if (!run) return null;
    setSkippingId(step.id);
    try {
      const res = await fetchApi('dataMigrationSkipStep', { runId: run.id, stepId: step.id });
      setRun(res.run);
      setSteps(res.steps || []);
      if (!silent) {
        setNotice(`${step.table_name} skipped — it won't block the rest of the run.`);
        loadHistory();
      }
      return res;
    } catch (ex) {
      if (!silent) setErr(errText(ex, 'Could not skip step'));
      return null;
    } finally {
      setSkippingId(null);
    }
  };

  /**
   * Run every remaining step in order, deciding Run vs Skip per step: try the copy, and
   * if it FAILS auto-skip that table so the rest of the run continues — a failed step
   * would otherwise block everything after it. The skipped row keeps its error and
   * recommended fix. Only a transport error (server unreachable) aborts the loop.
   */
  const runAll = async () => {
    if (!run) return;
    setErr('');
    setNotice('');
    setRunningAll(true);
    let copied = 0;
    let skipped = 0;
    let aborted = false;
    try {
      let current = steps;
      for (;;) {
        const next = current.find((s) => RUNNABLE.includes(s.status));
        if (!next) break;
        const res = await runStep(next, { silent: true });
        if (!res) { aborted = true; break; }
        if (res.lastStep && res.lastStep.ok === false) {
          const sres = await skipStep(next, { silent: true });
          if (!sres) { aborted = true; break; }
          skipped += 1;
          current = sres.steps || [];
        } else {
          copied += 1;
          current = res.steps || [];
        }
      }
    } finally {
      setRunningAll(false);
      loadHistory();
    }
    const parts = [];
    if (copied) parts.push(`${copied} copied`);
    if (skipped) parts.push(`${skipped} skipped (failed)`);
    if (aborted) setErr("Run all stopped — couldn't reach the server. Check the connection and run again.");
    else setNotice(parts.length ? `Run all finished · ${parts.join(' · ')}` : 'Nothing left to run.');
  };

  const openRun = async (id) => {
    setErr('');
    try {
      const res = await fetchApi('dataMigrationRun', { id });
      setRun(res.run);
      setSteps(res.steps || []);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (ex) {
      setErr(errText(ex, 'Could not load run'));
    }
  };

  const finishedCount = useMemo(() => history.filter((h) => FINISHED.includes(h.status)).length, [history]);

  const clearFinished = async () => {
    setClearing(true);
    try {
      const res = await fetchApi('dataMigrationClear');
      if (run && FINISHED.includes(run.status)) { setRun(null); setSteps([]); }
      setNotice(`${res?.deleted ?? 0} migration(s) removed.`);
      setConfirm(null);
      loadHistory();
    } catch (ex) {
      setErr(errText(ex, 'Could not clear migrations'));
    } finally {
      setClearing(false);
    }
  };

  const deleteRun = async (h) => {
    setClearing(true);
    try {
      await fetchApi('dataMigrationDeleteRun', { id: h.id });
      if (run && String(run.id) === String(h.id)) { setRun(null); setSteps([]); }
      setHistory((prev) => prev.filter((r) => r.id !== h.id));
      setNotice('Migration record removed.');
      setConfirm(null);
    } catch (ex) {
      setErr(errText(ex, 'Could not delete migration'));
    } finally {
      setClearing(false);
    }
  };

  // A skipped step is resolved, so it counts toward progress even though the run's
  // executed_steps only tracks successful copies.
  const doneCount = useMemo(() => steps.filter((s) => s.status === 'success' || s.status === 'skipped').length, [steps]);
  const skippedCount = useMemo(() => steps.filter((s) => s.status === 'skipped').length, [steps]);
  const progressPct = run && run.total_steps > 0 ? Math.round((doneCount / run.total_steps) * 100) : 0;

  const stepColumns = [
    { key: 'seq', label: '#', render: (s) => <span className="font-mono opacity-50">{s.seq}</span> },
    {
      key: 'table_name',
      label: 'Table',
      render: (s) => (
        <button
          type="button"
          onClick={() => run?.id && navigate(`/tools/data-migration/${run.id}/${s.table_name}`)}
          className="text-primary-600 font-medium hover:underline text-left"
          title="View row-level diff & reconcile"
        >
          {s.table_name}
        </button>
      ),
    },
    {
      key: 'counts',
      label: 'Source → Target',
      render: (s) => (
        <span className="opacity-70">
          {s.source_count ?? '—'} <ChevronRight className="inline opacity-40" size={11} /> {s.target_count ?? '—'}
        </span>
      ),
    },
    {
      key: 'rows_written',
      label: 'Copied',
      render: copiedCell,
    },
    {
      key: 'status',
      label: 'Status',
      render: (s) => (
        <div>
          <Badge variant={STATUS_VARIANT[s.status] || 'default'}>{s.status}</Badge>
          {s.resume_cursor && s.status !== 'success' && s.status !== 'skipped' ? (
            <div className="text-[11px] text-primary-600 mt-1">checkpoint saved · resumable</div>
          ) : null}
          {(s.status === 'failed' || s.status === 'skipped') && s.error_message ? (
            <div className={`text-[11px] mt-1 max-w-sm break-words ${s.status === 'skipped' ? 'text-warning-600' : 'text-error-600'}`}>
              {s.status === 'skipped' ? <span className="font-medium">skipped after error: </span> : null}
              {s.error_message}
            </div>
          ) : null}
          {(s.status === 'failed' || s.status === 'skipped') && s.recommendation ? (
            <div className="mt-1.5 max-w-sm rounded-md border border-warning-200 bg-warning-50 px-2 py-1.5 text-[11px] text-warning-800 flex gap-1.5">
              <Lightbulb size={13} className="shrink-0 mt-0.5 text-warning-500" />
              <span><span className="font-semibold">{s.error_title || 'Suggested fix'}: </span>{s.recommendation}</span>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'action',
      label: '',
      render: (s) => {
        if (s.status === 'success') return <CheckCircle2 className="text-success-600" size={18} />;
        if (s.status === 'skipped') {
          return <span className="inline-flex items-center text-xs text-warning-600"><SkipForward size={14} className="mr-1" />skipped</span>;
        }
        const isNext = s.id === nextStepId;
        return (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant={isNext ? 'primary' : 'secondary'}
              disabled={!isNext || !!executingId || runningAll || !!skippingId}
              loading={executingId === s.id}
              onClick={() => runStep(s)}
              leftIcon={<Play size={14} />}
            >
              {runLabel(s)}
            </Button>
            {isNext ? (
              <Button
                size="sm"
                variant="secondary"
                title="Skip this table and continue the run"
                disabled={!!executingId || runningAll}
                loading={skippingId === s.id}
                onClick={() => skipStep(s)}
                leftIcon={<SkipForward size={14} />}
              >
                Skip
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  const historyColumns = [
    {
      key: 'id',
      label: 'Run',
      render: (h) => (
        <button type="button" className="text-primary-600 font-medium hover:underline" onClick={() => openRun(h.id)}>#{h.id}</button>
      ),
    },
    { key: 'status', label: 'Status', render: (h) => <Badge variant={RUN_STATUS_VARIANT[h.status] || 'default'}>{RUN_STATUS_LABEL[h.status] || h.status}</Badge> },
    {
      key: 'source_label',
      label: 'Source → Target',
      render: (h) => <span className="font-mono text-xs break-all">{h.source_label || 'source'} → {h.target_label}</span>,
    },
    { key: 'mode', label: 'Mode' },
    { key: 'progress', label: 'Progress', render: (h) => `${h.executed_steps}/${h.total_steps} · ${h.total_rows_written} rows` },
    { key: 'created_by_name', label: 'By' },
    {
      key: 'delete',
      label: '',
      render: (h) => (
        <button
          type="button"
          onClick={() => setConfirm({ kind: 'delete', run: h })}
          title="Delete this migration record"
          className="p-1 opacity-40 hover:opacity-100 hover:text-error-600"
        >
          <Trash2 size={15} />
        </button>
      ),
    },
  ];

  // Extracted from the JSX: a three-way choice would otherwise be a nested ternary,
  // which this repo's lint config rejects.
  let historyPanel = <Table data={history} columns={historyColumns} pagination withAction={false} />;
  if (loadingHistory && history.length === 0) historyPanel = <Loader text="Loading history…" />;
  else if (history.length === 0) historyPanel = <p className="text-sm opacity-50">No migrations run yet.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <DatabaseZap size={22} className="text-primary-600" />
        <div>
          <h1 className="text-xl font-bold">Data Migration</h1>
          <p className="text-sm opacity-70">
            Copy row data from a source database into a target. Pick tables, choose a write mode,
            then sync table by table.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* type=password keeps the embedded credentials off-screen. The server stores
              them encrypted and only ever returns a masked label. */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                label="Source DB URL (read from)"
                type="password"
                placeholder="mysql://user:pass@host:3306/source_db"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => testConnection('source')} loading={testing === 'source'} disabled={!!testing} leftIcon={<PlugZap size={16} />}>Test</Button>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                label="Target DB URL (receives the data)"
                type="password"
                placeholder="mysql://user:pass@host:3306/target_db"
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => testConnection('target')} loading={testing === 'target'} disabled={!!testing} leftIcon={<PlugZap size={16} />}>Test</Button>
          </div>
        </div>

        {sourceUrl || targetUrl ? (
          <div className="flex justify-end -mt-1">
            <button type="button" onClick={() => { setSourceUrl(''); setTargetUrl(''); }} className="inline-flex items-center gap-1 text-xs opacity-50 hover:opacity-100">
              <Eraser size={13} /> Clear saved
            </button>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* twreact's Select hands onChange the RAW VALUE, not a DOM event — see the
              note in Scraper.jsx. Reading e.target.value here would silently never update. */}
          <Select
            label="Write mode"
            value={mode}
            options={MODES}
            onChange={(v) => setMode(v?.target ? v.target.value : (v?.value ?? v))}
          />
          <div className="flex flex-col justify-end gap-1.5">
            <Checkbox
              label="Ignore foreign keys during copy"
              checked={disableFk}
              onChange={(e) => setDisableFk(e?.target ? e.target.checked : Boolean(e))}
            />
            <p className="text-[11px] opacity-60">
              Recommended for a full clone — copies rows even when a parent table isn't there yet,
              so missing-parent and cyclic FKs won't block. Turn off to enforce FK constraints.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
            <span className="text-sm font-medium">
              Tables <span className="opacity-50 font-normal">({selected.length} selected{allTables.length ? ` of ${allTables.length}` : ''})</span>
              {loadedLabel ? <span className="ml-2 text-[11px] opacity-50 font-mono">from {loadedLabel}</span> : null}
            </span>
            <div className="flex items-center gap-2">
              {allTables.length > 0 ? (
                <Button size="sm" variant="secondary" onClick={autoSelectAll} leftIcon={<ListChecks size={14} />} title="Select every table, ordered by foreign keys">
                  Auto select
                </Button>
              ) : null}
              <Button size="sm" variant="secondary" onClick={loadTables} loading={tablesLoading} disabled={!sourceUrl.trim()} leftIcon={<DatabaseZap size={14} />}>
                {allTables.length ? 'Reload tables' : 'Load tables from source'}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-2">
            <span className="text-[11px] opacity-50 self-center mr-1">Quick add:</span>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => addPreset(p.tables)}
                className="inline-flex items-center gap-1 border border-secondary-200 dark:border-secondary-700 rounded-md px-2 py-0.5 text-xs font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <Plus size={12} /> {p.label}
              </button>
            ))}
          </div>

          {selected.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <span className="text-[11px] opacity-50 self-center mr-1">Copy order:</span>
              {selected.map((t, i) => (
                <span key={t} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 rounded-md px-2 py-0.5 text-xs font-medium">
                  <span className="opacity-50">{i + 1}.</span>
                  {t}
                  <button type="button" onClick={() => toggle(t)} className="hover:opacity-70"><X size={12} /></button>
                </span>
              ))}
              <button type="button" onClick={() => setSelected([])} className="text-xs opacity-50 hover:opacity-100 px-1">clear all</button>
            </div>
          ) : null}

          <div className="relative mb-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50 z-10" />
            <Input className="pl-8" placeholder="Search tables…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          <div className="max-h-56 overflow-y-auto border border-secondary-200 dark:border-secondary-700 rounded-lg divide-y divide-secondary-100 dark:divide-secondary-800">
            {allTables.length === 0 && (
              <p className="text-sm opacity-50 p-3">Enter the source URL, then <b>Load tables from source</b> to list its tables.</p>
            )}
            {allTables.length > 0 && filtered.length === 0 && (
              <p className="text-sm opacity-50 p-3">No tables match.</p>
            )}
            {allTables.length > 0 && filtered.length > 0
              && filtered.map((t) => (
                <label key={t.table} className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary-50 dark:hover:bg-secondary-800 cursor-pointer text-sm">
                  <input type="checkbox" checked={selected.includes(t.table)} onChange={() => toggle(t.table)} />
                  <span className="font-medium">{t.table}</span>
                  <span className="text-[11px] opacity-50">
                    {t.columns?.length || 0} cols
                    {t.foreignKeys?.length ? ` · ${t.foreignKeys.length} FK` : ''}
                    {t.primaryKey?.length ? ` · PK ${t.primaryKey.join(', ')}` : ' · no PK'}
                  </span>
                </label>
              ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={plan} loading={planning} leftIcon={<ArrowRightLeft size={16} />}>Plan migration</Button>
        </div>
      </Card>

      {err ? <Alert variant="error" title="Data Migration">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="Data Migration">{notice}</Alert> : null}

      {run ? (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={RUN_STATUS_VARIANT[run.status] || 'default'}>{RUN_STATUS_LABEL[run.status] || run.status}</Badge>
              <span className="text-sm font-mono opacity-70 break-all">{run.source_label || 'source'} → {run.target_label}</span>
              <Badge variant="default">{run.mode}</Badge>
              {Number(run.disable_fk_checks) ? (
                <Badge variant="default" title="Foreign-key enforcement is disabled on the target during this copy">FK checks off</Badge>
              ) : null}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {run.executed_steps}/{run.total_steps} tables · {run.total_rows_written} rows
                {run.failed_steps > 0 ? <span className="text-error-600"> · {run.failed_steps} failed</span> : null}
                {skippedCount > 0 ? <span className="text-warning-600"> · {skippedCount} skipped</span> : null}
              </span>
              {nextStepId ? (
                <Button size="sm" onClick={runAll} loading={runningAll} disabled={!!executingId} leftIcon={<Play size={14} />}>Run all</Button>
              ) : null}
            </div>
          </div>

          <div className="w-full h-2 bg-secondary-100 dark:bg-secondary-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor(run, skippedCount)}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <Table data={steps} columns={stepColumns} withAction={false} />
        </Card>
      ) : null}

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Recent migrations</h2>
          <div className="flex items-center gap-3">
            {finishedCount > 0 ? (
              <button
                type="button"
                onClick={() => setConfirm({ kind: 'clear' })}
                title="Delete all completed or failed migration records"
                className="inline-flex items-center gap-1 text-xs opacity-50 hover:opacity-100 hover:text-error-600"
              >
                <Trash2 size={13} /> Clear finished
              </button>
            ) : null}
            <button type="button" className="opacity-50 hover:opacity-100" onClick={loadHistory} title="Refresh">
              <RefreshCw size={15} className={loadingHistory ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {historyPanel}
      </Card>

      {confirm ? (
        <Dialog
          isOpen
          onClose={() => setConfirm(null)}
          title={confirm.kind === 'clear' ? 'Clear finished migrations?' : 'Delete this migration record?'}
        >
          <div className="space-y-4">
            <p className="text-sm">
              {confirm.kind === 'clear'
                ? `This removes ${finishedCount} completed or failed record(s).`
                : `This removes the record for ${confirm.run?.source_label || 'source'} → ${confirm.run?.target_label}.`}
              {' '}Local audit rows only — your source and target databases are not touched.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="destructive"
                loading={clearing}
                onClick={() => (confirm.kind === 'clear' ? clearFinished() : deleteRun(confirm.run))}
              >
                {confirm.kind === 'clear' ? 'Clear finished' : 'Delete'}
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
